import React, {
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Provider } from 'react-redux';
import styled, { createGlobalStyle, ThemeProvider } from 'styled-components';
import {
  createBrowserRouter, RouterProvider, useNavigate, useLocation,
} from 'react-router-dom';
import { LegalStyles, LoadingScreen, theme, StyleSheetManager } from '@mesa-labs/mesa-ui';

import Routes from '../routes';
import Navigation, { BackLink, BackLinkIcon } from './common/Navigation';
import { useSelector } from '../redux/hooks';
import { getImpersonatedVendorId } from '../redux/slices/auth';
import cognitoService from '../cognito';
import { combineReducers, configureStore, createReducer } from '@reduxjs/toolkit';
import persistStore from 'redux-persist/es/persistStore';
import { FLUSH, PAUSE, PERSIST, PURGE, REGISTER, REHYDRATE } from 'redux-persist';
import Header from './common/Header';
import { PageHeaderOverrideProvider } from './common/PageHeaderContext';
import { concatenatedMiddleware } from '../redux/store';
import ErrorBoundary from './ErrorBoundary';

const GlobalStyle = createGlobalStyle`
  html, body, #root {
    height: 100%;
  }

  #modal-root {
    left: 0;
    position: absolute;
    top: 0;
    z-index: 60;
  }

  #notification-root {
    left: 0;
    position: absolute;
    top: 0;
    z-index: 32;
  }

  body {
    font-family: ${(props) => props.theme.fonts.inter};
    padding: 0px;
    margin: 0px;

    input:focus {
      outline: none;
    }
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type=number] {
    appearance: textfield;
    -moz-appearance: textfield;
  }

  h1, h2, h3, h4, h5, h6, p {
    margin: 0px;
    padding: 0px;
  }

  * {
    scrollbar-width: thin;
    scrollbar-color: ${(props) => props.theme.colors.Gray200} transparent;
  }

  *::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  *::-webkit-scrollbar-track {
    background: transparent;
  }

  *::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.colors.Gray200};
    border-radius: 3px;
  }

  *::-webkit-scrollbar-thumb:hover {
    background: ${(props) => props.theme.colors.Gray300};
  }

  a {
    text-decoration: none;
    color: inherit;
  }

  * {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;

    box-sizing: border-box;
  }
  .react-calendar {
    width: 350px;
    max-width: 100%;
    background: white;
    border: 1px solid #a0a096;
    font-family: Arial, Helvetica, sans-serif;
    line-height: 1.125em;
    z-index: +1;
  }
  .react-calendar--doubleView {
    width: 700px;
  }
  .react-calendar--doubleView .react-calendar__viewContainer {
    display: table;
    margin: -0.5em;
    z-index: +1;
  }
  .react-calendar--doubleView .react-calendar__viewContainer > * {
    width: 50%;
    margin: 0.5em;
    z-index: +1;
  }
  .react-calendar,
  .react-calendar *,
  .react-calendar *:before,
  .react-calendar *:after {
    -moz-box-sizing: border-box;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
  }
  .react-calendar button {
    margin: 0;
    border: 0;
    outline: none;
  }
  .react-calendar button:enabled:hover {
    cursor: pointer;
  }
  .react-calendar__navigation {
    display: table;
    height: 44px;
    margin-bottom: 1em;
  }
  .react-calendar__navigation button {
    min-width: 44px;
    background: none;
  }
  .react-calendar__navigation button:enabled:hover,
  .react-calendar__navigation button:enabled:focus {
    background-color: #e6e6e6;
  }
  .react-calendar__navigation button[disabled] {
    background-color: #f0f0f0;
  }
  .react-calendar__month-view__weekdays {
    text-align: center;
    text-transform: uppercase;
    font-weight: bold;
    font-size: 0.75em;
  }
  .react-calendar__month-view__weekdays__weekday {
    padding: 0.5em;
  }
  .react-calendar__month-view__weekNumbers .react-calendar__tile {
    display: table;
    align-items: center;
    justify-content: center;
    font-size: 0.75em;
    font-weight: bold;
    padding: calc(0.75em / 0.75) calc(0.5em / 0.75);
  }
  .react-calendar__month-view__days__day--weekend {
    color: #d10000;
  }
  .react-calendar__month-view__days__day--neighboringMonth {
    color: #757575;
  }
  .react-calendar__year-view .react-calendar__tile,
  .react-calendar__decade-view .react-calendar__tile,
  .react-calendar__century-view .react-calendar__tile {
    padding: 2em 0.5em;
  }
  .react-calendar__tile {
    max-width: 100%;
    text-align: center;
    padding: 0.75em 0.5em;
    background: none;
  }
  .react-calendar__tile:disabled {
    background-color: #f0f0f0;
  }
  .react-calendar__tile:enabled:hover,
  .react-calendar__tile:enabled:focus {
    background-color: #e6e6e6;
  }
  .react-calendar__tile--now {
    background: #ffff76;
  }
  .react-calendar__tile--now:enabled:hover,
  .react-calendar__tile--now:enabled:focus {
    background: #ffffa9;
  }
  .react-calendar__tile--hasActive {
    background: #76baff;
  }
  .react-calendar__tile--hasActive:enabled:hover,
  .react-calendar__tile--hasActive:enabled:focus {
    background: #a9d4ff;
  }
  .react-calendar__tile--active {
    background: #006edc;
    color: white;
  }
  .react-calendar__tile--active:enabled:hover,
  .react-calendar__tile--active:enabled:focus {
    background: #1087ff;
  }
  .react-calendar--selectRange .react-calendar__tile--hover {
    background-color: #e6e6e6;
  }
`;

const Layout = styled.div`
  font-size: 16px;

  height: 100dvh;
  display: flex;
  flex-direction: column;

  overflow: hidden;
`;

const ContentContainer = styled.div`
  flex: 1;
  min-height: 0;

  display: flex;
  flex-direction: column;
`;

const NavigationContainer = styled.div`
  flex: 1;
  min-height: 0;

  display: flex;
  flex-direction: column;
`;

const MainContainer = styled.main`
  flex: 1;
  min-height: 0;

  padding: 0 32px 32px;

  overflow: auto;
`;

const routeToHeader: [RegExp, string][] = [
  [/^\/vendors$/, 'Vendors'],
  [/^\/vendors\/.+\/documents\/.+/, 'Vendor Document Details'],
  [/^\/vendors\/.+\/serviceAgreements\/.+/, 'Service Agreement Details'],
  [/^\/vendors\/.+$/, 'Vendor Details'],
  [/^\/enablement-v2$/, 'Enablement V2'],
  [/vendor-surveys$/, 'Vendor Surveys'],
  [/^\/invoices$/, 'Invoices'],
  [/programs$/, 'Programs'],
  [/invoices\/.+\/documents\/.+/, 'Invoice Document Details'],
  [/invoice-line-items$/, 'Invoice Line Items'],
  [/invoice-line-items\/.*/, 'Invoice Line Item Details'],
  [/customer-batch-transfers/, 'Transfers'],
  [/onboardings$/, 'Onboardings'],
  [/onboardings\/.*/, 'Onboarding Details'],
  [/businesses\/.*/, 'Business Details'],
  [/journal/, 'Journal'],
  [/projections/, 'Projections'],
  [/tasks/, 'Tasks'],
  [/external-vendor-statistics/, 'External Vendor Statistics'],
  [/data-reconciliation/, 'Data Reconciliation'],
  [/configuration-settings/, 'Configuration Settings'],
  [/^\/faq-configuration(\/|$)/, 'FAQ Configuration'],
  [/key-performance-indicators/, 'KPIs'],
  [/accelerated-payout/, 'Accelerated Payout Statistics'],
  [/adjustments$/, 'Adjustments'],
  [/adjustments\/.*/, 'Adjustment Details'],
  [/^\/underwriting\/transaction-labels$/, 'Underwriting - Transaction Labels'],
  [/^\/underwriting\/transaction-labels\/.*/, 'Transaction Label Details'],
  [/adjustment-groups$/, 'Adjustment Groups'],
  [/adjustment-groups\/.*/, 'Adjustment Group Details'],
  [/invoice-adjustment-approvals/, 'Invoice Adjustment Approvals'],
  [/vendor-documents/, 'Vendor Documents'],
  [/^\/collections$/, 'Collections'],
  [/^\/collections\/.*/, 'Collection Details'],
  [/reconciliation$/, 'Reconciliation'],
  [/batch-sweeps/, 'Batch Sweeps'],
  [/reconciliation\/.*/, 'Reconciliation Details'],
  [/partner-service-agreements\/.*\/create$/, 'Create Partner Service Agreement'],
  [/partner-service-agreements\/.*\/versions\/.*\/update$/, 'Update Partner Service Agreement'],
  [/service-agreements$/, 'Service Agreement History'],
  [/service-agreements\/compare\/.*/, 'Service Agreement Comparison'],
  [/service-agreements\/.*\/create$/, 'Create Service Agreement'],
  [/^\/clients\/.*/, 'Clients'],
  [/external-clients$/, 'External Clients'],
  [/external-clients\/.*/, 'External Client Details'],
  [/external-vendors$/, 'External Vendors'],
  [/external-client-vendors$/, 'External Client Vendors'],
  [/internal-clients$/, 'Internal Clients'],
  [/internal-clients\/.*/, 'Internal Client Details'],
  [/internal-vendors$/, 'Internal Vendors'],
  [/internal-client-vendors$/, 'Internal Client Vendors'],
  [/facilitators/, 'Facilitators'],
  [/abandoned-users/, 'Abandoned Users'],
  [/ondemand-inflights/, 'On-Demand/In-Flights'],
  [/revenue/, 'Revenue'],
  [/cohorts/, 'Cohorts'],
  [/settings/, 'Settings'],
  [/^\/factoring-collections$/, 'Factoring Collections'],
  [/^\/scheduled-vendor-collections\/.*/, 'Scheduled Vendor Collection Details'],
  [/^\/invoices\/.*\/scheduled-invoice-collections/, 'Scheduled Invoice Collections'],
  [/^\/invoices\/.+$/, 'Invoice Details'],
  [/^\/permissions$/, 'Permissions'],
  [/program-promotions/, 'Program Promotions'],
  [/^\/factoring-access$/, 'Factoring Access'],
  [/^\/partner-rev-shares$/, 'Partner Rev Share Configs'],
  [/^\/partner-rev-shares\/.*/, 'Partner Rev Share Config'],
  [/vendor-sub-accounts/, 'Vendor Sub-Accounts']
];

const routeToBackButton: RegExp[] = [
  /vendors\/.*/,
  /invoices\/.*/,
  /invoice-line-items\/.*/,
  /onboardings\/.*/,
  /collections\/.*/,
  /reconciliation\/.*/,
  /invoices\/.*\/documents\/.*/,
  /businesses\/.*/,
  /adjustments\/.*/,
  /adjustment-groups\/.*/,
  /underwriting\/transaction-labels\/.*/,
  /external-clients\/.*/,
  /internal-clients\/.*/,
  /vendors\/.*\/documents\/.*/,
  /vendors\/.*\/serviceAgreements\/.*/,
  /service-agreements\/.*/,
  /partner-service-agreements\/.*/,
  /partner-rev-shares\/.*/,
  /partners\/.*/,
];

function Content({ children }: { children: ReactNode }): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const authAttributes = useSelector((state) => state.auth.attributes);
  const [headerOverride, setHeaderOverride] = useState<string | undefined>(undefined);

  const pageHeader = useMemo(() => {
    if (headerOverride) {
      return headerOverride;
    }

    const match = routeToHeader.find((route) => route[0].test(location.pathname));

    if (match) {
      return match[1];
    }

    return null;
  }, [location.pathname, headerOverride]);


  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const partnerId = useSelector((state) => state.auth.partnerId);
  const isImpersonatingVendor = useMemo(() => isLoggedIn ? getImpersonatedVendorId(authAttributes) : undefined, [isLoggedIn, authAttributes]);
  const isImpersonating = useMemo(() => Boolean(isImpersonatingVendor || (isLoggedIn && partnerId)), [isImpersonatingVendor, isLoggedIn, partnerId]);
  const showBackButton = useMemo(() => routeToBackButton.find((route) => route.test(location.pathname)), [location.pathname]);

  return (
    <ContentContainer>
      <NavigationContainer>
        <Navigation />
        <MainContainer>
          {showBackButton && (
            <BackLink onClick={() => navigate(-1)}>
              <BackLinkIcon src="/assets/back-icon.svg" />
              Back
            </BackLink>
          )}
          {pageHeader && <Header>{pageHeader}</Header>}
          <PageHeaderOverrideProvider setHeaderOverride={setHeaderOverride}>
            {children}
          </PageHeaderOverrideProvider>
        </MainContainer>
      </NavigationContainer>
    </ContentContainer>
  );
}

// A data router (rather than <BrowserRouter>) so pages can use useBlocker -- e.g. the email sequence
// pages' unsaved-changes navigation guard (MESA-4912). A single splat route renders the existing
// declarative <Routes> tree as descendant routes, so route definitions are unchanged.
const router = createBrowserRouter([
  {
    path: '*',
    element: (
      <Content>
        <Routes />
      </Content>
    ),
  },
]);

const handleAuth = async () => {
  try {
    const session = cognitoService.getCurrentSession();
    if (!session) {
      return;
    }

    const user = cognitoService.getCurrentUser();
    if (!user) {
      return;
    }

    const { createConfiguredStore } = await import('../redux/store');
    const store = createConfiguredStore();

    return store;
  } catch (err) {
    console.debug(`Error setting up authentication: ${err}. Please contact support with this information.`);
  }
};

function App(): ReactElement {
  const [loading, setLoading] = useState(false);
  const [store, setStore] = useState(configureStore({
    reducer: combineReducers({
      auth: createReducer({}, () => ({})),
      globalSearch: createReducer({}, () => ({})),
    }),
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }).concat(concatenatedMiddleware) as any
  }));
  const [persistor] = useState(persistStore(store));

  useEffect(() => {
    (async () => {
      setLoading(true);

      const authStore = await handleAuth();

      if (authStore) {
        setStore(authStore);
        persistor.persist();
      }

      setLoading(false);
    })();
  }, []);

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        <LoadingScreen />
      </ThemeProvider>
    );
  }

  return (
    <ErrorBoundary fallback={<div>Error: </div>}>
      <Provider store={store}>
        <StyleSheetManager>
          <ThemeProvider theme={theme}>
            <GlobalStyle />
            <LegalStyles />

            <Layout id="operations-ui">
              <RouterProvider router={router} />
            </Layout>
          </ThemeProvider>
        </StyleSheetManager>
      </Provider>
    </ErrorBoundary>
  );
}

export default App;
